'use client';

import {
  ArrowRight,
  BookOpen,
  Building2,
  ExternalLink,
  Globe2,
  Landmark,
  LogOut,
  MailOpen,
  ShieldCheck,
  UserRound,
} from 'lucide-react';
import Image from 'next/image';
import { useSession } from 'next-auth/react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';

import SfLogoMain from '@/assets/icons/sidebar-nav/sportsfi-logo-main';
import AuthLoadingScreen from '@/components/auth/AuthLoadingScreen';
import { Button } from '@/components/ui';
import { useInvitesGetCurrentUserPendingQuery, useOrganisationsGetByCurrentUserQuery } from '@/generated/graphql';
import {
  SIGNED_OUT_PATH,
  SPORTSFI_APP_CONFIG,
  type AccessibleOrganisationOption,
  type LocationLike,
  type SportsFiApp,
  type SportsFiUserOrganisation,
  buildSportsFiDevDocsUrl,
  buildSportsFiAppUrl,
  buildSportsFiOnboardingUrl,
  getAccessibleOrganisationOptions,
  getAppFromHostname,
  getTrustedSportsFiUrl,
  hasAccessToDevDocs,
  hasAccessToApp,
  isSportsFiAppSelectorUrl,
} from '@/lib/auth/appAccess';
import { getAuthCleanupPathWithLoginReturnUrl } from '@/lib/auth/clientRedirects';
import { isApolloAuthenticationError, isApolloIdentityProviderUnavailableError } from '@/lib/auth/sessionErrors';
import { clearSessionActiveOrgId, setSessionActiveOrgId } from '@/lib/utils/activeOrganisation';
import { handleSignOut } from '@/lib/utils/auth';

const DEFAULT_WORKSPACE_IMAGE_URL =
  'https://sportsfi-global-public.s3.eu-west-1.amazonaws.com/icons/contract-negotiate-icon.png';

function getCurrentLocation(): LocationLike {
  return {
    protocol: window.location.protocol,
    hostname: window.location.hostname,
    host: window.location.host,
  };
}

function getIconForApp(app: SportsFiApp): React.ComponentType<{ className?: string }> {
  switch (app) {
    case 'admin':
      return ShieldCheck;
    case 'club':
      return Building2;
    case 'player':
      return UserRound;
    case 'finance':
      return Landmark;
    case 'gpe':
      return Globe2;
  }
}

function getAppSubtitle(app: SportsFiApp): string {
  switch (app) {
    case 'admin':
      return 'Administrative workspace';
    case 'club':
      return 'Club workspace';
    case 'player':
      return 'Player workspace';
    case 'finance':
      return 'Funder workspace';
    case 'gpe':
      return 'GPE workspace';
  }
}

function normaliseImageUrl(imageUrl?: string | null): string | null {
  const trimmedImageUrl = imageUrl?.trim();

  if (trimmedImageUrl == null || trimmedImageUrl.length === 0 || trimmedImageUrl === 'MISSING_VARIABLE') {
    return null;
  }

  return trimmedImageUrl;
}

function getOrganisationImageUrl(option: AccessibleOrganisationOption): string | null {
  if (option.app === 'club') {
    return normaliseImageUrl(option.organisation.club?.imageUrl) ?? DEFAULT_WORKSPACE_IMAGE_URL;
  }

  if (option.app === 'player') {
    return normaliseImageUrl(option.organisation.player?.imageUrl) ?? DEFAULT_WORKSPACE_IMAGE_URL;
  }

  return null;
}

function getSelectorCallbackPath(): string {
  return `${window.location.pathname}${window.location.search}`;
}

function getSignedInUserLabel(session: ReturnType<typeof useSession>['data']): string | null {
  const userName = session?.user?.name?.trim();
  const userEmail = session?.user?.email?.trim();

  if (userName && userEmail) {
    return `${userName} (${userEmail})`;
  }

  return userName || userEmail || null;
}

type SystemWorkspaceTone = 'admin' | 'docs' | 'invite';

function getSystemWorkspaceButtonClassName(tone: SystemWorkspaceTone): string {
  switch (tone) {
    case 'admin':
      return 'group flex min-h-24 w-full items-center gap-4 rounded-md border border-cyan-300/45 bg-cyan-400/15 p-4 text-left transition hover:border-cyan-200 hover:bg-cyan-400/25 focus:outline-none focus:ring-2 focus:ring-cyan-300';
    case 'docs':
      return 'group flex min-h-24 w-full items-center gap-4 rounded-md border border-amber-300/50 bg-amber-300/15 p-4 text-left transition hover:border-amber-200 hover:bg-amber-300/25 focus:outline-none focus:ring-2 focus:ring-amber-200';
    case 'invite':
      return 'group flex min-h-24 w-full items-center gap-4 rounded-md border border-amber-300/50 bg-amber-300/15 p-4 text-left transition hover:border-amber-200 hover:bg-amber-300/25 focus:outline-none focus:ring-2 focus:ring-amber-200';
  }
}

function getSystemWorkspaceIconClassName(tone: SystemWorkspaceTone): string {
  switch (tone) {
    case 'admin':
      return 'flex size-11 shrink-0 items-center justify-center rounded-md bg-cyan-300 text-black shadow-[0_0_22px_rgba(103,232,249,0.28)]';
    case 'docs':
      return 'flex size-11 shrink-0 items-center justify-center rounded-md bg-amber-300 text-black shadow-[0_0_22px_rgba(252,211,77,0.24)]';
    case 'invite':
      return 'flex size-11 shrink-0 items-center justify-center rounded-md bg-amber-300 text-black shadow-[0_0_22px_rgba(252,211,77,0.24)]';
  }
}

function SystemWorkspaceButton({
  label,
  subtitle,
  Icon,
  TrailingIcon = ArrowRight,
  tone,
  onClick,
}: {
  label: string;
  subtitle: string;
  Icon: React.ComponentType<{ className?: string }>;
  TrailingIcon?: React.ComponentType<{ className?: string }>;
  tone: SystemWorkspaceTone;
  onClick: () => void;
}) {
  return (
    <button type="button" onClick={onClick} className={getSystemWorkspaceButtonClassName(tone)}>
      <span className={getSystemWorkspaceIconClassName(tone)}>
        <Icon className="size-5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-base font-semibold">{label}</span>
        <span className="mt-1 block text-sm text-white/75">{subtitle}</span>
      </span>
      <TrailingIcon className="size-5 shrink-0 text-white/60 transition group-hover:text-white" />
    </button>
  );
}

function WorkspaceImage({ imageUrl, organisationId }: { imageUrl: string; organisationId: string }) {
  const [currentImageUrl, setCurrentImageUrl] = useState(imageUrl);

  useEffect(() => {
    setCurrentImageUrl(imageUrl);
  }, [imageUrl]);

  return (
    <span
      data-testid={`workspace-image-${organisationId}`}
      aria-hidden="true"
      className="flex size-11 shrink-0 items-center justify-center overflow-hidden rounded-md border border-white/20 bg-white"
    >
      <Image
        src={currentImageUrl}
        alt=""
        width={44}
        height={44}
        unoptimized
        className="h-full w-full object-contain p-1"
        onError={() => {
          if (currentImageUrl !== DEFAULT_WORKSPACE_IMAGE_URL) {
            setCurrentImageUrl(DEFAULT_WORKSPACE_IMAGE_URL);
          }
        }}
      />
    </span>
  );
}

export default function AuthResolutionContent(): React.ReactNode {
  const router = useRouter();
  const searchParams = useSearchParams();
  const queryReturnUrl = searchParams.get('returnUrl');
  const { data: session, status } = useSession();
  const [currentLocation, setCurrentLocation] = useState<LocationLike | null>(null);
  const selectorReturnUrl = queryReturnUrl;
  const requiresReauth = session?.error === 'RefreshAccessTokenError';
  const shouldLoadAccessOptions = status === 'authenticated' && !requiresReauth;
  const { data, loading, error } = useOrganisationsGetByCurrentUserQuery({
    skip: !shouldLoadAccessOptions,
    fetchPolicy: 'network-only',
  });
  const {
    data: pendingInvitesData,
    loading: pendingInvitesLoading,
    error: pendingInvitesError,
  } = useInvitesGetCurrentUserPendingQuery({
    skip: !shouldLoadAccessOptions,
    fetchPolicy: 'network-only',
  });
  const hasInvalidAuthSessionError =
    isApolloAuthenticationError(error) || isApolloIdentityProviderUnavailableError(error);

  useEffect(() => {
    setCurrentLocation(getCurrentLocation());
  }, []);

  useEffect(() => {
    if (status !== 'unauthenticated') {
      return;
    }

    const loginUrl = new URL('/login', window.location.origin);

    loginUrl.searchParams.set('callbackUrl', getSelectorCallbackPath());
    router.replace(`${loginUrl.pathname}${loginUrl.search}`);
  }, [router, status]);

  useEffect(() => {
    if (status !== 'authenticated' || !requiresReauth) {
      return;
    }

    window.location.replace(getAuthCleanupPathWithLoginReturnUrl(window.location.href));
  }, [requiresReauth, status]);

  useEffect(() => {
    if (currentLocation == null || !hasInvalidAuthSessionError) {
      return;
    }

    window.location.replace(getAuthCleanupPathWithLoginReturnUrl(window.location.href));
  }, [currentLocation, hasInvalidAuthSessionError]);

  const targetReturnUrl = useMemo(() => {
    if (!currentLocation || !selectorReturnUrl) {
      return null;
    }

    const trustedUrl = getTrustedSportsFiUrl(selectorReturnUrl, currentLocation);

    if ((trustedUrl != null && isSportsFiAppSelectorUrl(trustedUrl)) || trustedUrl?.pathname === '/unauthorized') {
      return null;
    }

    return trustedUrl;
  }, [currentLocation, selectorReturnUrl]);

  const targetApp = targetReturnUrl ? getAppFromHostname(targetReturnUrl.hostname) : null;
  const userRoles = session?.userRoles ?? [];
  const signedInUserLabel = getSignedInUserLabel(session);
  const organisations = (data?.organisationsGetByCurrentUser ?? []) as SportsFiUserOrganisation[];

  const allOrganisationOptions = getAccessibleOrganisationOptions(organisations, userRoles);
  const matchingTargetOrganisationOptions =
    targetApp && targetApp !== 'admin' ? allOrganisationOptions.filter((option) => option.app === targetApp) : [];
  const organisationOptions =
    matchingTargetOrganisationOptions.length > 0 ? matchingTargetOrganisationOptions : allOrganisationOptions;
  const hasAdmin = hasAccessToApp(userRoles, 'admin');
  const devDocsUrl = currentLocation ? buildSportsFiDevDocsUrl(currentLocation) : null;
  const hasDevDocs = devDocsUrl != null && hasAccessToDevDocs(userRoles);
  const showAdminOption = hasAdmin;
  const systemOptionCount = (showAdminOption ? 1 : 0) + (hasDevDocs ? 1 : 0);
  const pendingInviteCount = pendingInvitesData?.invitesGetCurrentUserPending.length ?? 0;
  const hasPendingInvites = pendingInviteCount > 0 && !pendingInvitesError;
  const hasAnyOption = organisationOptions.length > 0 || systemOptionCount > 0 || hasPendingInvites;
  const canRenderPanel =
    status === 'authenticated' &&
    currentLocation != null &&
    session != null &&
    !requiresReauth &&
    !hasInvalidAuthSessionError &&
    !loading &&
    !pendingInvitesLoading &&
    (data != null || error);
  const optionCount = organisationOptions.length + systemOptionCount + (hasPendingInvites ? 1 : 0);
  const cardMaxWidth = optionCount > 1 ? 'max-w-3xl' : 'max-w-2xl';
  const optionGridClassName = 'grid gap-3 text-left';

  const handleOrganisationSelect = (option: AccessibleOrganisationOption) => {
    if (!currentLocation) {
      return;
    }

    setSessionActiveOrgId(option.organisation.organisationId, { persistCookie: true });

    const targetUrl =
      targetApp === option.app && targetReturnUrl
        ? targetReturnUrl.toString()
        : buildSportsFiAppUrl(option.app, option.config.defaultPath, currentLocation);

    window.location.assign(targetUrl);
  };

  const handleAdminSelect = () => {
    if (!currentLocation) {
      return;
    }

    clearSessionActiveOrgId();

    const targetUrl =
      targetApp === 'admin' && targetReturnUrl
        ? targetReturnUrl.toString()
        : buildSportsFiAppUrl('admin', SPORTSFI_APP_CONFIG.admin.defaultPath, currentLocation);

    window.location.assign(targetUrl);
  };

  const handleDevDocsSelect = () => {
    if (!devDocsUrl) {
      return;
    }

    clearSessionActiveOrgId();
    window.location.assign(devDocsUrl);
  };

  const handlePendingInvitesSelect = () => {
    if (!currentLocation) {
      return;
    }

    window.location.assign(buildSportsFiOnboardingUrl('/invitations', currentLocation));
  };

  const handleSignOutClick = () => {
    handleSignOut({ callbackUrl: SIGNED_OUT_PATH, skipConfirmation: true });
  };

  if (!canRenderPanel) {
    return <AuthLoadingScreen />;
  }

  return (
    <>
      <Image
        src="/images/bg_lights.png"
        alt="SportsFi Background"
        fill
        priority
        className="object-cover brightness-50"
      />

      <div className="relative z-10 flex min-h-screen flex-col items-center justify-center px-4 py-10 text-center text-white">
        <div className="mb-8">
          <SfLogoMain />
        </div>

        {canRenderPanel ? (
          <section
            className={`w-full ${cardMaxWidth} rounded-md border border-white/15 bg-black/55 p-6 shadow-xl backdrop-blur`}
          >
            {error ? (
              <div className="space-y-4">
                <p className="rounded-md border border-red-300/50 bg-red-950/60 p-3 text-sm text-red-100">
                  We could not load your SportsFi access. Sign out and sign in again.
                </p>
                <Button
                  type="button"
                  variant="secondary"
                  icon={LogOut}
                  onClick={handleSignOutClick}
                  className="min-w-40 px-6 py-3 text-base"
                >
                  Sign out
                </Button>
              </div>
            ) : hasAnyOption ? (
              <>
                {signedInUserLabel ? (
                  <p className="mb-8 text-sm text-white/70">
                    Signed in as <span className="font-semibold text-white">{signedInUserLabel}</span>
                  </p>
                ) : null}

                <div className={optionGridClassName}>
                  {hasPendingInvites ? (
                    <SystemWorkspaceButton
                      label="Pending invitations"
                      subtitle={`${pendingInviteCount} invitation${pendingInviteCount === 1 ? '' : 's'} awaiting completion`}
                      Icon={MailOpen}
                      tone="invite"
                      onClick={handlePendingInvitesSelect}
                    />
                  ) : null}

                  {showAdminOption ? (
                    <SystemWorkspaceButton
                      label="Admin app"
                      subtitle="Administrative workspace"
                      Icon={ShieldCheck}
                      tone="admin"
                      onClick={handleAdminSelect}
                    />
                  ) : null}

                  {hasDevDocs ? (
                    <SystemWorkspaceButton
                      label="Developer docs"
                      subtitle="Engineering and platform documentation"
                      Icon={BookOpen}
                      TrailingIcon={ExternalLink}
                      tone="docs"
                      onClick={handleDevDocsSelect}
                    />
                  ) : null}

                  {organisationOptions.map((option) => {
                    const Icon = getIconForApp(option.app);
                    const imageUrl = getOrganisationImageUrl(option);

                    return (
                      <button
                        type="button"
                        key={`${option.app}-${option.organisation.organisationId}`}
                        onClick={() => handleOrganisationSelect(option)}
                        className="group flex min-h-24 w-full items-center gap-4 rounded-md border border-white/15 bg-white/10 p-4 text-left transition hover:border-cyan-300 hover:bg-white/15 focus:outline-none focus:ring-2 focus:ring-cyan-300"
                      >
                        {imageUrl ? (
                          <WorkspaceImage imageUrl={imageUrl} organisationId={option.organisation.organisationId} />
                        ) : (
                          <span className="flex size-11 shrink-0 items-center justify-center rounded-md bg-white text-black">
                            <Icon className="size-5" />
                          </span>
                        )}
                        <span className="min-w-0 flex-1">
                          <span className="flex flex-wrap items-center gap-2">
                            <span className="break-words text-base font-semibold">{option.organisation.name}</span>
                            {option.organisation.isDefault ? (
                              <span className="rounded-sm border border-cyan-200/40 px-2 py-0.5 text-[0.625rem] uppercase tracking-normal text-cyan-100">
                                Default
                              </span>
                            ) : null}
                          </span>
                          <span className="mt-1 block text-sm text-white/70">
                            {SPORTSFI_APP_CONFIG[option.app].label} app - {getAppSubtitle(option.app)}
                          </span>
                        </span>
                        <ArrowRight className="size-5 shrink-0 text-white/50 transition group-hover:text-cyan-200" />
                      </button>
                    );
                  })}
                </div>

                <Button
                  type="button"
                  variant="secondary"
                  icon={LogOut}
                  onClick={handleSignOutClick}
                  className="mt-8 min-w-40 px-6 py-3 text-base"
                >
                  Sign out
                </Button>
              </>
            ) : (
              <div className="space-y-4">
                {signedInUserLabel ? (
                  <p className="text-sm text-white/70">
                    Signed in as <span className="font-semibold text-white">{signedInUserLabel}</span>
                  </p>
                ) : null}
                <p className="rounded-md border border-amber-300/40 bg-amber-950/40 p-3 text-sm text-amber-50">
                  Your signed-in account does not have access to any SportsFi organisations. Choose another account or
                  ask an administrator to update your organisation access.
                </p>
                <Button
                  type="button"
                  variant="secondary"
                  icon={LogOut}
                  onClick={handleSignOutClick}
                  className="min-w-40 px-6 py-3 text-base"
                >
                  Sign out
                </Button>
              </div>
            )}
          </section>
        ) : null}
      </div>
    </>
  );
}
